/**
 * Permission-based filtering utilities for UI-only data filtering.
 *
 * The backend returns ALL data for calendar-essential endpoints (same-salon security).
 * The frontend filters what users SEE based on their sectionPermissions from /access.
 *
 * @see docs/FRONTEND_MIGRATION_GUIDE.md for full documentation
 */

export type ScopeValue = "own" | "all";

export interface FilterOptions {
  currentUserId: string;
  scope: ScopeValue;
}

/**
 * Generic interface for items with a staff_id field
 */
interface StaffOwnedItem {
  staff_id?: string;
  employee_id?: string;
}

/**
 * Transaction/appointment structure
 */
interface TransactionLineItem {
  booked_services?: Array<{ staff_id?: string }>;
  booked_products?: Array<{ staff_id?: string }>;
}

interface Transaction {
  id: string;
  line_items?: TransactionLineItem;
  created_by?: string;
  staff_id?: string;
}

/**
 * Time off/time block structure
 */
interface TimeOffRecord {
  id: string;
  staff_id?: string;
  employee_id?: string;
}

/**
 * Staff member structure
 */
interface StaffMember {
  id: string;
  [key: string]: unknown;
}

/**
 * Staff service entry within a service
 */
interface StaffService {
  id: string;
  staff_id: string;
  staff_name?: string;
  price?: number;
  base_duration_mins?: number;
  [key: string]: unknown;
}

/**
 * Service structure with nested servicing_staff array
 */
interface Service {
  id: string;
  name?: string;
  servicing_staff?: StaffService[];
  // Legacy field name (some places may use staff_services)
  staff_services?: Array<{ staff_id?: string }>;
  [key: string]: unknown;
}

/**
 * Check if a transaction belongs to the current user.
 * A transaction "belongs" to a user if they performed any service in it.
 */
export function isOwnTransaction(
  transaction: Transaction,
  currentUserId: string,
): boolean {
  // Check if user is the creator (for quick sales)
  if (transaction.created_by === currentUserId) {
    return true;
  }

  // Check if user performed any booked service
  const bookedServices = transaction.line_items?.booked_services || [];
  if (bookedServices.some((bs) => bs.staff_id === currentUserId)) {
    return true;
  }

  // Check direct staff_id on transaction
  if (transaction.staff_id === currentUserId) {
    return true;
  }

  return false;
}

/**
 * Filter transactions/appointments based on calendar.scope.
 * "own" = only show appointments where user is the service provider
 * "all" = show all appointments
 */
export function filterTransactionsByScope<T extends Transaction>(
  transactions: T[] | null | undefined,
  options: FilterOptions,
): T[] {
  if (!transactions) return [];

  if (options.scope === "all") {
    return transactions;
  }

  // scope === 'own': filter to appointments where user performed a service
  return transactions.filter((transaction) =>
    isOwnTransaction(transaction, options.currentUserId),
  );
}

/**
 * Check if a time off record belongs to the current user.
 */
export function isOwnTimeOff(
  record: TimeOffRecord,
  currentUserId: string,
): boolean {
  return (
    record.staff_id === currentUserId || record.employee_id === currentUserId
  );
}

/**
 * Filter time off blocks based on calendar.scope.
 * "own" = only show user's own time off
 * "all" = show all time off
 */
export function filterTimeOffByScope<T extends TimeOffRecord>(
  timeOffRecords: T[] | null | undefined,
  options: FilterOptions,
): T[] {
  if (!timeOffRecords) return [];

  if (options.scope === "all") {
    return timeOffRecords;
  }

  return timeOffRecords.filter((record) =>
    isOwnTimeOff(record, options.currentUserId),
  );
}

/**
 * Filter staff list based on settingsStaff.scope.
 * "own" = only show current user's profile
 * "all" = show all staff
 */
export function filterStaffByScope<T extends StaffMember>(
  staffList: T[] | null | undefined,
  options: FilterOptions,
): T[] {
  if (!staffList) return [];

  if (options.scope === "all") {
    return staffList;
  }

  return staffList.filter((staff) => staff.id === options.currentUserId);
}

/**
 * Filter services based on settingsServices.scope.
 *
 * When scope is "own":
 * - Only show services where the current user is in servicing_staff
 * - Only show the user's own staff_service entry in servicing_staff array
 *
 * When scope is "all":
 * - Show all services with all staff entries
 */
export function filterServicesByScope<T extends Service>(
  services: T[] | null | undefined,
  options: FilterOptions,
): T[] {
  if (!services) return [];

  if (options.scope === "all") {
    return services;
  }

  // scope === 'own': filter to services where user provides the service
  // and only show user's own staff_service entry
  return (
    services
      .map((service) => {
        // Check servicing_staff (primary field name from API)
        const servicingStaff = service.servicing_staff || [];
        // Also check staff_services (legacy/alternate field name)
        const staffServices = service.staff_services || [];

        // Filter to only include current user's entries
        const filteredServicingStaff = servicingStaff.filter(
          (staff) => staff.staff_id === options.currentUserId,
        );
        const filteredStaffServices = staffServices.filter(
          (staff) => staff.staff_id === options.currentUserId,
        );

        return {
          ...service,
          servicing_staff: filteredServicingStaff,
          staff_services: filteredStaffServices,
        } as T;
      })
      // Only include services where user has at least one staff_service
      .filter((service) => {
        const hasServicingStaff = (service.servicing_staff?.length || 0) > 0;
        const hasStaffServices = (service.staff_services?.length || 0) > 0;
        return hasServicingStaff || hasStaffServices;
      })
  );
}

/**
 * Get the effective scope for a section, defaulting to "own" if not available.
 */
export function getEffectiveScope(
  scope: ScopeValue | "none" | undefined | null,
): ScopeValue {
  if (scope === "all") return "all";
  return "own";
}
